import { format } from 'date-fns';
import { 
  CalendarDays as CalendarIcon, Plus, Play, Edit3, Trash2, Camera, 
  Clock, Coffee, AlertTriangle 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription } from '../ui/alert-dialog';

export const ManualEntryDialog = ({ 
  open, onClose, manualEntry, setManualEntry, projects, onSave, calculateDuration 
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-lg rounded-2xl">
      <DialogHeader>
        <DialogTitle className="text-xl">Add Manual Entry</DialogTitle>
        <DialogDescription>Record time that you've already worked</DialogDescription>
      </DialogHeader>
      <div className="space-y-5 pt-4">
        <div className="space-y-2">
          <Label className="label-premium">Description <span className="text-red-500">*</span></Label>
          <Input
            placeholder="What did you work on?"
            value={manualEntry.description}
            onChange={(e) => setManualEntry(prev => ({ ...prev, description: e.target.value }))}
            className="input-premium"
            data-testid="manual-description-input"
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label className="label-premium">Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start h-11 rounded-xl font-normal">
                <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
                {format(manualEntry.date, 'PPP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={manualEntry.date}
                onSelect={(date) => date && setManualEntry(prev => ({ ...prev, date }))}
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="label-premium">Start Time</Label>
            <Input
              type="time"
              value={manualEntry.start_time}
              onChange={(e) => setManualEntry(prev => ({ ...prev, start_time: e.target.value }))}
              className="input-premium"
              data-testid="manual-start-time-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="label-premium">End Time</Label>
            <Input
              type="time"
              value={manualEntry.end_time}
              onChange={(e) => setManualEntry(prev => ({ ...prev, end_time: e.target.value }))}
              className="input-premium"
              data-testid="manual-end-time-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="label-premium">Duration</Label>
            <div className="h-11 px-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center">
              <span className="text-sm font-semibold text-slate-700">{calculateDuration()}</span>
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label className="label-premium">Project <span className="text-red-500">*</span></Label>
          <Select 
            value={manualEntry.project_id || 'none'} 
            onValueChange={(v) => setManualEntry(prev => ({ ...prev, project_id: v === 'none' ? '' : v }))}
          >
            <SelectTrigger className="h-11 rounded-xl" data-testid="manual-project-select">
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map(project => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: project.color }} />
                    {project.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label className="label-premium">Notes (optional)</Label>
          <Textarea
            placeholder="Additional notes..."
            value={manualEntry.notes}
            onChange={(e) => setManualEntry(prev => ({ ...prev, notes: e.target.value }))}
            rows={2}
            className="resize-none rounded-xl"
          />
        </div>
        
        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={() => onClose(false)} className="flex-1 h-11 rounded-xl">
            Cancel
          </Button>
          <Button onClick={onSave} className="flex-1 btn-premium h-11" data-testid="manual-save-btn">
            Save Entry
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const EditEntryDialog = ({ 
  entry, editForm, setEditForm, projects, onSave, onClose 
}) => (
  <Dialog open={!!entry} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="sm:max-w-lg rounded-2xl">
      <DialogHeader>
        <DialogTitle className="text-xl">Edit Time Entry</DialogTitle>
        <DialogDescription>Update the details of this time entry</DialogDescription>
      </DialogHeader>
      <div className="space-y-5 pt-4">
        <div className="space-y-2">
          <Label className="label-premium">Description <span className="text-red-500">*</span></Label>
          <Input
            placeholder="What did you work on?"
            value={editForm.description}
            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
            className="input-premium"
            data-testid="edit-description-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="label-premium">Project</Label>
          <Select 
            value={editForm.project_id || "none"} 
            onValueChange={(val) => setEditForm(prev => ({ ...prev, project_id: val === "none" ? "" : val }))}
          >
            <SelectTrigger className="h-11 rounded-xl">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Project</SelectItem>
              {projects.map(project => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color || '#3b82f6' }} />
                    {project.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="label-premium">Start Time</Label>
            <Input
              type="time"
              value={editForm.start_time}
              onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
              className="input-premium"
              data-testid="edit-start-time-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="label-premium">End Time</Label>
            <Input
              type="time"
              value={editForm.end_time}
              onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
              className="input-premium"
              data-testid="edit-end-time-input"
            />
          </div>
        </div>
        
        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-11 rounded-xl">
            Cancel
          </Button>
          <Button onClick={onSave} className="flex-1 btn-premium h-11" data-testid="edit-save-btn">
            Save Changes
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const IdleAlertDialog = ({ 
  open, onOpenChange, idleTime, formatHours, onKeep, onDiscard, onBreak 
}) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="rounded-2xl">
      <AlertDialogHeader>
        <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-7 h-7 text-amber-600" />
        </div>
        <AlertDialogTitle className="text-center text-xl">
          You've been away
        </AlertDialogTitle>
        <AlertDialogDescription className="text-center">
          No activity detected for <span className="font-semibold text-slate-700 dark:text-slate-300">{formatHours(idleTime)}</span>.
          <br />
          What would you like to do with this idle time?
          <br />
          <span className="text-xs text-amber-600 mt-2 block">
            Timer will auto-stop if no action is taken.
          </span>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div className="flex flex-col gap-2 mt-4">
        <Button onClick={onKeep} className="w-full h-11 rounded-xl btn-premium">
          <Clock className="w-4 h-4 mr-2" />
          Keep Idle Time
        </Button>
        <Button variant="outline" onClick={onDiscard} className="w-full h-11 rounded-xl">
          <Trash2 className="w-4 h-4 mr-2" />
          Discard Idle Time
        </Button>
        <Button variant="ghost" onClick={onBreak} className="w-full h-11 rounded-xl text-slate-600">
          <Coffee className="w-4 h-4 mr-2" />
          Add as Break
        </Button>
      </div>
    </AlertDialogContent>
  </AlertDialog>
);

export const TimeEntryRow = ({ 
  entry, project, onContinue, onEdit, onDelete, onViewScreenshots, formatDuration, formatTimeOnly 
}) => (
  <div 
    className="group flex items-center gap-4 px-5 py-4 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
    data-testid={`time-entry-${entry.id}`}
  >
    <div 
      className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white dark:ring-slate-900"
      style={{ backgroundColor: project?.color || '#94a3b8' }}
    />
    
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
        {entry.description || <span className="text-slate-400 dark:text-slate-500 italic font-normal">No description added</span>}
      </p>
      <div className="flex items-center gap-2 mt-0.5">
        {project && (
          <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
            {project.name}
          </span>
        )}
        {entry.entry_type === 'manual' && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            entry.approval_status === 'pending_approval' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' :
            entry.approval_status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300' :
            entry.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300' : ''
          }`} data-testid={`approval-status-${entry.id}`}>
            {entry.approval_status === 'pending_approval' ? 'Pending Approval' :
             entry.approval_status === 'rejected' ? 'Rejected' :
             entry.approval_status === 'approved' ? 'Approved' : ''}
          </span>
        )}
      </div>
    </div>
    
    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      {entry.screenshot_count > 0 && (
        <Button
          variant="ghost" size="sm"
          className="h-8 px-2.5 rounded-lg text-slate-600 hover:text-blue-600 hover:bg-blue-50"
          onClick={() => onViewScreenshots(entry.id)}
          data-testid={`view-screenshots-${entry.id}`}
        >
          <Camera className="w-3.5 h-3.5 mr-1" />
          {entry.screenshot_count}
        </Button>
      )}
      <Button
        variant="ghost" size="sm"
        className="h-8 px-2.5 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        onClick={() => onContinue(entry)}
        data-testid={`continue-entry-${entry.id}`}
      >
        <Play className="w-3.5 h-3.5 mr-1" />
        Continue
      </Button>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg hover:bg-slate-100" onClick={() => onEdit(entry)}>
        <Edit3 className="w-3.5 h-3.5" />
      </Button>
      <Button
        variant="ghost" size="sm"
        className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
        onClick={() => onDelete(entry.id)}
        data-testid={`delete-entry-${entry.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
    
    <div className="text-right flex-shrink-0">
      <p className="text-sm font-bold text-slate-900 dark:text-white timer-display">
        {formatDuration(entry.duration)}
      </p>
      <p className="text-xs text-slate-500">
        {formatTimeOnly(entry.start_time)} – {entry.end_time ? formatTimeOnly(entry.end_time) : 'Running'}
      </p>
    </div>
  </div>
);
